import { useRef } from "react";

/**
 * Decides whether a make/model cascade list is worth fetching again.
 *
 * Each picker in the cascade fetches its list from the Autocomplete's `onOpen`, and MUI fires that on
 * every reopen — including the reopens it forces on us, because a `pending` request empties the list,
 * which disables the field, which closes the popup. Unguarded, that costs a request every time the
 * broker reopens a picker or types after it has been closed, even though every list is keyed by the
 * fields above it and cannot have changed in between.
 *
 * A fetch is worth making only when nothing is already in flight for that list, and either those
 * fields moved or the last answer came back empty — an empty list still has to be retryable.
 */
export const useCascadeListFetch = () => {
  const lastRequest = useRef<Record<string, string>>({});

  return (listKey: string, request: object, isLoading: boolean, hasResults: boolean) => {
    // A raised loader only means "in flight" for a request this picker actually made. The asset can
    // arrive with one already raised, and nothing lowers a flag whose request was never sent — so
    // trusting it before the first fetch would leave the picker waiting on nobody's request forever.
    if (isLoading && lastRequest.current[listKey] !== undefined) return false;

    const requestKey = JSON.stringify(request);
    if (lastRequest.current[listKey] === requestKey && hasResults) return false;

    lastRequest.current[listKey] = requestKey;
    return true;
  };
};
